import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  Form,
  Grid,
  Input,
  Row,
  Table,
  Descriptions,
  Checkbox,
} from 'antd';
import {
  Button,
  COLORS,
  Text,
  Title,
} from 'components';
import { scrollToTop } from 'components/ScrollToTop';
import {
  LoadingOutlined,
  SearchOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';
import NetPaybutton, {
  getNetPayBtn,
  paymentLocationsEnum,
  validateLastTransaction,
} from 'views/Payments/components/NetPayButton';
import { useSelector } from 'react-redux';
import Previewer from 'components/Previewer';
import useQuery from 'hooks/useQuery';
import {
  getCargosPadron,
  getConvenios,
  getParcialidades,
  postReferenciasBanco,
} from 'api/recaudacion';
import formatReference from 'utils/formatReference';
import { toCurrency } from 'utils/formatters';
import round from 'utils/round';
import Spinner from 'components/Spinner';
import ModalBancos from 'views/Payments/components/ModalBancos';
import { selectPadrones } from 'store/catalogos';
import logger from 'utils/logger';
import { gettersListMap } from 'api/padrones';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import { padronesEnum } from 'views/Tramites/GPM/enums';

const getNoSaldados = (arr = []) => arr.filter((item) => !item.saldado);

const Convenios = () => {
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [visiblePreviewRecibo, setVisiblePreviewRecibo] = useState(false);
  const [visiblePreviewOrden, setVisiblePreviewOrden] = useState(false);
  const [generandoRecibo, setGenerandoRecibo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recibo, setRecibo] = useState();
  const [folio] = useQuery(['folio']);
  const [visibleBancos, setVisibleBancos] = useState(false);
  const [isNewFolio, setIsNewFolio] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [parcialidades, setParcialidades] = useState([]);
  const [convenio, setConvenio] = useState(null);
  const [padron, setPadron] = useState(null);
  const [referencia, setReferencia] = useState({ data: '', folio: '' });
  const [cargos, setCargos] = useState([]);
  const padrones = useSelector(selectPadrones);

  useEffect(() => {
    if (folio) {
      form.setFieldsValue({ folio });
      setTimeout(() => form.submit());
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setGenerandoRecibo(false);
    }, 10000);
    validateLastTransaction(false, async (_referencia) => {
      const _recibo = await formatReference(_referencia);
      setTimeout(() => {
        setRecibo(_recibo);
        setVisiblePreviewRecibo(true);
      }, 1000);
    }, setLoading, paymentLocationsEnum.PAYMENT_ORDER, true, setGenerandoRecibo);
  }, []);

  const onCancel = () => {
    setVisiblePreviewRecibo(false);
    setVisiblePreviewOrden(false);
    setVisibleBancos(false);
    setGenerandoRecibo(false);
    setIsNewFolio(true);
    setSelectedRowKeys([]);
    setReferencia({ data: '', folio: '' });
    setPadron(null);
    setConvenio();
    setParcialidades([]);
    form.resetFields();
    scrollToTop(screens);
  };

  const generarReferenciaBanco = async (
    referenciaNormal = true,
    fromNetpay = false,
  ) => {
    setLoading(true);
    const _referencia = await postReferenciasBanco({
      tipoDePadron: padron?.tipo_de_padron,
      padron,
      padrones,
      cargos,
      parcialidades: parcialidades.filter((p) => selectedRowKeys.includes(p.id)),
      totalProp: 'importe',
      referencia_normal: referenciaNormal,
      convenio: convenio?.id,
      total: parcialidades.filter((p) => selectedRowKeys.includes(p.id))
        .reduce((accum, curr) => accum + curr.importe_de_la_parcialidad, 0),
    });
    setReferencia(_referencia);
    if (fromNetpay) {
      setTimeout(() => getNetPayBtn()?.click(), 1500);
    }
    if (!referenciaNormal) {
      setVisibleBancos(true);
    }
    setLoading(false);
    return _referencia;
  };

  const getPropNameContactInfo = (tipoDePadron) => {
    const names = {
      [padronesEnum.CIUDADANO]: 'self',
      [padronesEnum.CONTRIBUYENTE]: 'self',
    };
    return names?.[tipoDePadron] ?? 'contribuyente';
  };

  const onFinishSearch = async () => {
    try {
      setLoading(true);
      const { folio: _folio } = form.getFieldsValue();
      const [_convenio] = await getConvenios({ folio: _folio });
      if (!_convenio) {
        throw new Error('No se encontró el convenio.');
      }

      localStorage.setItem(`${paymentLocationsEnum.PAYMENT_ORDER}_CONVENIO`, _convenio.id);
      const _parcialidades = await getParcialidades(_convenio.id);
      const response = await gettersListMap[_convenio.padron_conveniado]({
        id: _convenio.object_id_padron,
      });
      const _padron = Array.isArray(response) ? response[0] : response?.results[0];
      const [, _cargos] = await getCargosPadron({
        padron: _padron.tipo_de_padron,
        padron_id: _padron.id,
      });

      setCargos(_cargos.filter((c) => c.convenios === _convenio.id));
      const propNameContactInfo = getPropNameContactInfo(_padron.tipo_de_padron);
      setPadron({ ..._padron, propNameContactInfo });
      setConvenio(_convenio);
      setParcialidades(_parcialidades);

      if (_parcialidades.some((e) => !e.saldado)) {
        const first = getNoSaldados(_parcialidades).map((p) => p.id)[0];
        setSelectedRowKeys([first]);
      }
    } catch (error) {
      logger(error);
    } finally {
      setLoading(false);
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const getBase64 = () => {
    if (visiblePreviewRecibo) {
      return recibo?.base64;
    }
    if (visiblePreviewOrden) {
      return `data:application/pdf;base64,${referencia?.data}`;
    }
    return null;
  };

  const [total] = useMemo(() => {
    const selected = parcialidades.filter((p) => selectedRowKeys.includes(p.id));
    const _total = selected.reduce((accum, curr) => accum + curr.importe_de_la_parcialidad, 0);
    return round(_total);
  }, [selectedRowKeys]);

  const generarOrdenDePago = async () => {
    setLoading(true);
    if (!referencia?.folio) {
      const _referencia = await generarReferenciaBanco();

      setReferencia(_referencia);
    }
    setVisiblePreviewOrden(true);
    setLoading(false);
  };

  return (
    <>
      <Col span={24}>
        <Title style={{ textAlign: 'center', margin: 0 }}>
          Pago de convenios
        </Title>
      </Col>
      <Card
        title={(
          <Row justify="space-between">
            <Col>
              <Title level={2} $accent>
                {convenio ? 'Información del convenio' : 'Buscar convenio'}
              </Title>
            </Col>
            <Col>
              {convenio && (
                <Button
                  onClick={onCancel}
                  danger
                  type="link"
                  style={{ fontWeight: 'bold' }}
                >
                  <Text>
                    Limpiar búsqueda
                  </Text>
                  <DeleteOutlined />
                </Button>
              )}
            </Col>
          </Row>
        )}
        style={{ width: '100%' }}
        bordered
      >
        <Col span={24}>
          <Row gutter={[10, 40]}>
            {convenio && (
            <>
              <Col span={24}>
                <Descriptions
                  bordered
                  size="small"
                  column={screens.md ? 2 : 1}
                >
                  {convenio?.nombre_del_ciudadano && (
                    <>
                      <Descriptions.Item label="Clave ciudadano">
                        {convenio?.nombre_del_ciudadano?.split(' - ')[0]}
                      </Descriptions.Item>
                      <Descriptions.Item label="Nombre">
                        {convenio?.nombre_del_ciudadano?.split(' - ')[1]}
                      </Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Clave padrón">
                    {padron?.clave || padron?.clave_ciudadana }
                  </Descriptions.Item>
                  <Descriptions.Item label="Padrón">
                    {padrones.find((p) => p.id === convenio.padron_conveniado)?.descripcion}
                  </Descriptions.Item>
                  <Descriptions.Item label="Folio convenio">
                    {convenio?.folio}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fecha alta">
                    {convenio?.fecha_de_creacion?.format(DATETIME_FRONTEND_FORMAT)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fecha de último pago">
                    {convenio?.fecha_ultimo_pago?.format(DATETIME_FRONTEND_FORMAT) || 'Sin pagos'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Importe total">
                    {toCurrency(convenio?.total)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Número de pagos">
                    {convenio?.numero_de_pagos}
                  </Descriptions.Item>
                  <Descriptions.Item label="Importe pagado">
                    {toCurrency(convenio?.pagado)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pagos vencidos">
                    {convenio?.pagos_vencidos}
                  </Descriptions.Item>
                  <Descriptions.Item label="Importe adeudado">
                    {toCurrency(convenio?.adeudo)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Observaciones">
                    {convenio?.observaciones}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              {parcialidades.every((e) => e.saldado) ? (
                <Row justify="end" gutter={[10, 10]} style={{ width: '100%' }}>
                  {/* <Button
                    block={!screens.md}
                    onClick={() => {
                    }}
                    loading={loading}
                    $accent
                  >
                  TODO: Implementar este botón
                    Ver Recibo
                    <EyeOutlined />
                  </Button> */}
                  <Title level={2} $accent>
                    Este convenio ya ha sido saldado
                  </Title>
                </Row>
              ) : (
                <Col span={24}>
                  <Row justify="end">
                    <Title level={2} $accent>
                      Total a pagar:
                      {' '}
                      {toCurrency(total)}
                    </Title>
                  </Row>
                </Col>
              )}
              <Col span={24}>
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={parcialidades}
                  bordered
                  loading={loading}
                  pagination={{
                    position: ['bottomLeft'],
                    pageSize: parcialidades.length,
                  }}
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: (keys) => {
                      const _selectedKeys = [...selectedRowKeys];
                      const _data = getNoSaldados(parcialidades);
                      const _filtered = _data.filter((item) => keys
                        .includes(item.id)).map((p) => p.id);
                      const _keys = _selectedKeys.length !== _filtered.length
                        ? _filtered : [_data.map((p) => p.id)[0]];
                      setSelectedRowKeys(_keys);
                    },
                    renderCell: (checked, record) => (
                      <Checkbox
                        checked={checked}
                        disabled={record.saldado || loading}
                      />
                    ),
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (record.saldado) return;

                      const prev = parcialidades
                        .filter((p) => p.mensualidad < record.mensualidad && !p.saldado)
                        .map((p) => p.id);

                      if (selectedRowKeys.includes(record.id)) {
                        const clone = [...selectedRowKeys];
                        clone.splice(selectedRowKeys.indexOf(record.id), 1);
                        setSelectedRowKeys([...prev]);
                      } else {
                        setSelectedRowKeys([...prev, record.id]);
                      }
                    },
                  })}
                  rowClassName={(record) => {
                    let className = '';
                    if (record.saldado) {
                      className = 'row-saldado';
                    }
                    return className;
                  }}
                  columns={[
                    {
                      title: 'Mensualidad',
                      dataIndex: 'mensualidad',
                      render: (val) => (val === 0 ? 'Anticipo' : val),
                    },
                    {
                      title: 'Fecha de vencimiento',
                      dataIndex: 'fecha_de_vencimiento',
                      render: (val) => val?.format(DATETIME_FRONTEND_FORMAT),
                    },
                    {
                      title: 'Fecha de pago',
                      dataIndex: 'fecha_de_pago',
                      render: (val) => val?.format(DATETIME_FRONTEND_FORMAT),
                    },
                    {
                      title: 'Importe de la parcialidad',
                      dataIndex: 'importe_de_la_parcialidad',
                      render: (val, record) => (
                        <b style={{ color: record?.saldado ? '' : COLORS.accent }}>
                          {toCurrency(val)}
                        </b>
                      ),
                      align: 'right',
                    },
                  ]}
                />
              </Col>
            </>
            )}
            {!convenio && (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinishSearch}
              style={{ width: '100%' }}
            >
              <Row justify="end" gutter={[10, 10]}>
                <Col span={24}>
                  <Form.Item
                    label="Folio"
                    name="folio"
                    rules={rules.required}
                  >
                    <Input
                      disabled={loading}
                      suffix={loading ? <LoadingOutlined /> : null}
                    />
                  </Form.Item>
                </Col>
                {!convenio && (
                <Col>
                  <Button
                    block={!screens.md}
                    htmlType="submit"
                    loading={loading}
                    $accent
                  >
                    Buscar
                    <SearchOutlined />
                  </Button>
                </Col>
                )}
              </Row>
            </Form>
            )}
            {convenio && parcialidades.some((e) => !e.saldado) && (
              <Row justify="end" gutter={[10, 10]} style={{ width: '100%' }}>
                <NetPaybutton
                  loading={loading}
                  padron={padron}
                  total={total}
                  cargos={parcialidades.filter((p) => selectedRowKeys.includes(p.id))}
                  createdBy="PAYMENT_ORDER"
                  totalProp="importe_de_la_parcialidad"
                  generarReferencia={() => generarReferenciaBanco(true, true)}
                  referencia={referencia?.folio}
                />
                <Button
                  block={!screens.md}
                  onClick={generarOrdenDePago}
                  loading={loading}
                  $accent
                  style={{ marginRight: screens.md ? 5 : 0 }}
                >
                  Orden de pago
                  <BarcodeOutlined />
                </Button>
                <Col xs={24} sm={24} md={8}>
                  <Button
                    onClick={() => generarReferenciaBanco(false)}
                    loading={loading}
                    block
                    $accent
                  >
                    Transferencia bancaria
                    <CreditCardOutlined />
                  </Button>
                </Col>
              </Row>
            )}
          </Row>
        </Col>
      </Card>
      <Previewer
        visible={visiblePreviewRecibo || visiblePreviewOrden}
        base64={getBase64()}
        onCancel={() => {
          if (visiblePreviewOrden) {
            setVisiblePreviewRecibo(false);
            setVisiblePreviewOrden(false);
          } else {
            onCancel();
          }
        }}
      />
      <Spinner
        visible={generandoRecibo}
        loading={loading}
        generado={!!recibo}
        retryFunc={() => window.location.reload()}
      />
      <ModalBancos
        visible={visibleBancos}
        onCancel={() => { setVisibleBancos(false); }}
        cargosGenerados={cargos}
        padron={padron?.id}
        tipoDePadron={padron?.tipo_de_padron}
        // currentClassifier={currentClassifier}
        padrones={padrones}
        cbReferenciaBanco={() => generarReferenciaBanco(false)}
        isNewFolio={isNewFolio}
        totalProp="total_real"
      />
    </>
  );
};

export default Convenios;
